import { useState, useEffect } from 'react';
import { createAppointment } from '../../services/appointments';
import { getCalendarBlocks } from '../../services/calendarBlocks';
import { getAppointments } from '../../services/appointments';
import { toLA_yyyy_mm_dd } from '../../utils/dateLA';
import { notifyEvent } from '../../services/messaging/notifyEvent';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  packageName?: string;
}

export default function ScheduleCallModal({ isOpen, onClose, packageName }: Props) {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    date: '',
    time_start: '',
    notes: '',
    package_interest: packageName || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [bookedDates, setBookedDates] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadBlockedAndBookedDates();
    }
  }, [isOpen]);

  const loadBlockedAndBookedDates = async () => {
    try {
      const [blocks, appointments] = await Promise.all([
        getCalendarBlocks(),
        getAppointments()
      ]);
      
      setBlockedDates(blocks.map(b => b.date));
      setBookedDates(
        appointments
          .filter(a => a.status !== 'canceled')
          .map(a => a.date)
      );
    } catch (err) {
      console.error('Error loading dates:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await createAppointment({
        ...formData,
        status: 'requested',
        notes: formData.package_interest 
          ? `Interesado en: ${formData.package_interest}\n\n${formData.notes}`
          : formData.notes
      });

      // Notificar al admin por email (en background)
      notifyEvent({
        event_type: 'appointment',
        data: {
          client_name: formData.client_name,
          client_email: formData.client_email,
          client_phone: formData.client_phone,
          date: formData.date,
          time_start: formData.time_start,
          notes: formData.package_interest 
            ? `Interesado en: ${formData.package_interest}\n\n${formData.notes}`
            : formData.notes,
        },
      }).catch(err => {
        console.error('Error enviando notificación de llamada:', err);
      });

      setSuccess(true);
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        date: '',
        time_start: '',
        notes: '',
        package_interest: packageName || ''
      });
      
      await loadBlockedAndBookedDates();
      
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Error al agendar la llamada. Por favor intenta de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toLA_yyyy_mm_dd(tomorrow);
  };

  const isDateDisabled = (dateStr: string) => {
    return blockedDates.includes(dateStr) || bookedDates.includes(dateStr);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Agendar Consulta Telefónica</h3>
            <p className="text-sm text-gray-600 mt-1">Te llamaremos para hablar sobre tu evento</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {packageName && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                <i className="ri-phone-line mr-2"></i>
                Consulta sobre: <strong>{packageName}</strong>
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="client_name" className="block text-sm font-medium text-gray-900 mb-2">
                Nombre completo *
              </label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                placeholder="Tu nombre"
              />
            </div>

            <div>
              <label htmlFor="client_phone" className="block text-sm font-medium text-gray-900 mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                id="client_phone"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label htmlFor="client_email" className="block text-sm font-medium text-gray-900 mb-2">
              Email
            </label>
            <input
              type="email"
              id="client_email"
              name="client_email"
              value={formData.client_email}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              placeholder="tu@email.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-900 mb-2">
                Fecha de la llamada *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={getMinDate()}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              />
              {formData.date && isDateDisabled(formData.date) && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-2">
                  <i className="ri-error-warning-fill"></i>
                  Esta fecha no está disponible
                </p>
              )}
            </div>

            <div>
              <label htmlFor="time_start" className="block text-sm font-medium text-gray-900 mb-2">
                Hora preferida *
              </label>
              <input
                type="time"
                id="time_start"
                name="time_start"
                value={formData.time_start}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-2">
              Notas adicionales
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none bg-white"
              placeholder="Cuéntanos brevemente sobre tu evento..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.notes.length}/500 caracteres
            </p>
          </div>

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-xl text-green-600"></i>
              <p className="text-sm text-green-800">
                ¡Llamada agendada! Te contactaremos en la fecha y hora indicada.
              </p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-xl text-red-600"></i>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (formData.date && isDateDisabled(formData.date))}
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? 'Agendando...' : 'Agendar Llamada'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            * Te llamaremos en la fecha y hora indicada para hablar sobre tu evento
          </p>
        </form>
      </div>
    </div>
  );
}
